import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  Paper,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material'
import { Add as AddIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import {
  useSubmitCigarMutation,
  useGetCigarSubmissionsQuery,
  CreateCigarSubmissionRequest,
} from '@store/api/submissionApi'

const WRAPPER_OPTIONS = [
  'Connecticut',
  'Habano',
  'Maduro',
  'Corojo',
  'Oscuro',
  'Sun Grown',
  'Cameroon',
  'Sumatra',
  'Natural',
  'Claro',
  'Colorado',
  'Other',
]

const STRENGTH_OPTIONS = ['Mild', 'Mild-Medium', 'Medium', 'Medium-Full', 'Full']

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  modification_requested: 'info',
}

const emptyForm: CreateCigarSubmissionRequest = {
  name: '',
  brand: '',
  vitola: '',
  wrapperType: '',
  originCountry: '',
  description: '',
  strengthLevel: '',
  notes: '',
}

const SubmitCigar: React.FC = () => {
  const [form, setForm] = useState<CreateCigarSubmissionRequest>(emptyForm)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [submitCigar, { isLoading: isSubmitting }] = useSubmitCigarMutation()
  const { data: submissions = [], isLoading: isLoadingHistory } = useGetCigarSubmissionsQuery()

  const handleChange = (field: keyof CreateCigarSubmissionRequest) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
    setSuccessMessage(null)
    setErrorMessage(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccessMessage(null)
    setErrorMessage(null)

    if (!form.name || !form.brand || !form.vitola || !form.wrapperType || !form.originCountry) {
      setErrorMessage('Please fill in all required fields.')
      return
    }

    try {
      await submitCigar(form).unwrap()
      setSuccessMessage(
        'Your cigar submission has been received! It will be reviewed by our team before appearing in the public database.'
      )
      setForm(emptyForm)
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } }
      setErrorMessage(error?.data?.message ?? 'Submission failed. Please try again.')
    }
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Submit a New Cigar
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Help grow the community database by submitting cigars that aren&apos;t listed yet. All
        submissions are reviewed before being added.
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Cigar Details
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {successMessage && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Cigar Name *"
              value={form.name}
              onChange={handleChange('name')}
              fullWidth
              required
              placeholder="e.g. Robusto"
            />
            <TextField
              label="Brand / Manufacturer *"
              value={form.brand}
              onChange={handleChange('brand')}
              fullWidth
              required
              placeholder="e.g. Arturo Fuente"
            />
            <TextField
              label="Vitola / Size *"
              value={form.vitola}
              onChange={handleChange('vitola')}
              fullWidth
              required
              placeholder="e.g. Robusto, Toro, Churchill"
            />
            <TextField
              label="Wrapper Type *"
              value={form.wrapperType}
              onChange={handleChange('wrapperType')}
              fullWidth
              required
              select
            >
              {WRAPPER_OPTIONS.map((w) => (
                <MenuItem key={w} value={w}>
                  {w}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Origin Country *"
              value={form.originCountry}
              onChange={handleChange('originCountry')}
              fullWidth
              required
              placeholder="e.g. Nicaragua, Dominican Republic"
            />
            <TextField
              label="Strength Level"
              value={form.strengthLevel}
              onChange={handleChange('strengthLevel')}
              fullWidth
              select
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              {STRENGTH_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={form.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description of this cigar..."
              sx={{ gridColumn: { sm: '1 / -1' } }}
            />
            <TextField
              label="Additional Notes"
              value={form.notes}
              onChange={handleChange('notes')}
              fullWidth
              multiline
              rows={2}
              placeholder="Any additional info for the reviewers..."
              sx={{ gridColumn: { sm: '1 / -1' } }}
            />
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Cigar'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Submission History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          My Submissions
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {isLoadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : submissions.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No submissions yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cigar</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Vitola</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {submissions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>{sub.name}</TableCell>
                    <TableCell>{sub.brand}</TableCell>
                    <TableCell>{sub.vitola}</TableCell>
                    <TableCell>
                      <Chip
                        label={sub.status.replace('_', ' ')}
                        color={STATUS_COLORS[sub.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(sub.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  )
}

export default SubmitCigar
